(function () {
  'use strict';

  const CONTACT_STAGE_SELECTORS = Object.freeze({
    title: '[data-contact-stage-title]',
    story: '[data-contact-stage-story]',
    why: '[data-contact-stage-why]',
    moments: '[data-contact-stage-moments]',
    closing: '[data-contact-stage-closing]'
  });

  const WAIT_STAGE_SELECTORS = Object.freeze({
    title: '[data-wait-title]',
    story: '[data-wait-story]',
    why: '[data-wait-why]',
    moments: '[data-wait-moments]',
    closing: '[data-wait-closing]'
  });

  function normalizeWaitExperience(instance, waitExperience) {
    const source = waitExperience && typeof waitExperience === 'object' ? waitExperience : {};
    const normalizeText = function normalizeText(value) {
      return typeof value === 'string' ? value.trim() : '';
    };
    const moments = Array.isArray(source.moments)
      ? source.moments.map(normalizeText).filter(Boolean).slice(0, 3)
      : [];

    return {
      title: normalizeText(source.title),
      story: normalizeText(source.story),
      whyWait: normalizeText(source.why_wait),
      moments,
      closingLine: normalizeText(source.closing_line)
    };
  }

  function setOptionalText(root, selector, value) {
    if (!root) return;
    const element = root.querySelector(selector);
    if (!element) return;
    element.textContent = value || '';
    element.hidden = !value;
  }

  function renderMoments(instance, root, selector, moments) {
    if (!root) return;
    const container = root.querySelector(selector);
    if (!container) return;

    container.innerHTML = moments.map(function renderMoment(moment, index) {
      const order = String(index + 1).padStart(2, '0');
      return `<div class="consultation-completion__moment consultation-wait__moment"><span class="consultation-completion__moment-index">${order}</span><p class="consultation-completion__moment-text consultation-wait__moment-text">${instance._escapeHtml(moment)}</p></div>`;
    }).join('');
    container.hidden = moments.length === 0;
  }

  function renderCompletionSurface(instance, root, selectors, content, fallbackTitle) {
    if (!root) return;

    const titleEl = root.querySelector(selectors.title);
    if (titleEl) titleEl.textContent = content.title || fallbackTitle || '';
    setOptionalText(root, selectors.story, content.story);
    setOptionalText(root, selectors.why, content.whyWait);
    renderMoments(instance, root, selectors.moments, content.moments);
    setOptionalText(root, selectors.closing, content.closingLine);
  }

  function getDeliveryState(instance) {
    const copy = typeof instance._getContactUiCopy === 'function' ? instance._getContactUiCopy() : {};
    const contact = instance._contact;

    if (!contact || !contact.type || !contact.value) {
      return {
        label: copy.deliverySkippedLabel || 'Finalizing your consultation',
        value: ''
      };
    }

    return {
      label: copy.deliveryLabel || 'Sending your consultation to',
      value: contact.type === 'mobile'
        ? (contact.normalizedValue || contact.value)
        : contact.value
    };
  }

  function renderDeliveryPanel(instance) {
    const waitRoot = instance.querySelector('[data-consultation-wait]');
    if (!waitRoot) return;
    const deliveryLabel = waitRoot.querySelector('[data-delivery-label]');
    const deliveryValue = waitRoot.querySelector('[data-delivery-value]');
    const state = getDeliveryState(instance);

    if (deliveryLabel) {
      deliveryLabel.textContent = state.label || '';
      deliveryLabel.hidden = !state.label;
    }

    if (deliveryValue) {
      deliveryValue.textContent = state.value || '';
      deliveryValue.hidden = !state.value;
      deliveryValue.classList.toggle('is-phone', !!(instance._contact && instance._contact.type === 'mobile' && state.value));
    }
  }

  function getAssessmentWaitDuration(instance) {
    const configured = Number(instance._assessmentWaitDurationMs);
    return Number.isFinite(configured) && configured > 0 ? configured : 30000;
  }

  function getProgressElements(instance) {
    const waitRoot = instance.querySelector('[data-consultation-wait]');
    if (!waitRoot) return {};

    return {
      root: waitRoot,
      progress: waitRoot.querySelector('[data-wait-progress]'),
      line: waitRoot.querySelector('[data-wait-progress-line]') || waitRoot.querySelector('.consultation-wait__progress-line'),
      percent: waitRoot.querySelector('[data-wait-progress-percent]'),
      label: waitRoot.querySelector('[data-wait-progress-label]'),
      stream: waitRoot.querySelector('[data-wait-stream]')
    };
  }

  function updateProgress(instance, percent) {
    const elements = getProgressElements(instance);
    if (!elements.root) return;

    const safePercent = Math.max(0, Math.min(100, Math.round(percent)));
    if (elements.percent) elements.percent.textContent = `${safePercent}%`;
    if (elements.line) elements.line.style.width = `${safePercent}%`;
    if (elements.progress) elements.progress.setAttribute('aria-valuenow', String(safePercent));
  }

  function buildWaitStreamPhrases(instance, content) {
    const t = instance.config && instance.config.i18n || {};
    const source = [
      content.story,
      content.whyWait,
      ...(content.moments || []),
      content.closingLine
    ].filter(Boolean).join(' ');
    const fallback = content.title || t.generatingAssessment || t.phaseGenerating || '';
    const text = source || fallback;
    const words = text.split(/\s+/).map(function normalizeWord(word) {
      return word.trim();
    }).filter(Boolean);

    if (!words.length) return [];

    const chunkSize = words.length > 28 ? 8 : 7;
    const phrases = [];
    for (let i = 0; i < words.length; i += chunkSize) {
      phrases.push(words.slice(i, i + chunkSize).join(' '));
    }
    return phrases.slice(0, 8);
  }

  function stopAssessmentWaitProgress(instance, options) {
    const state = instance._assessmentWaitProgressState;
    if (state) {
      if (state.intervalId) clearInterval(state.intervalId);
      if (state.streamTimeoutId) clearTimeout(state.streamTimeoutId);
      if (state.startFrameId) cancelAnimationFrame(state.startFrameId);
    }

    instance._assessmentWaitProgressState = null;

    if (options && options.complete) {
      updateProgress(instance, 100);
      const elements = getProgressElements(instance);
      const closingLine = instance._waitExperience && instance._waitExperience.closingLine;
      if (elements.stream && closingLine) elements.stream.textContent = closingLine;
    }
  }

  function startAssessmentWaitProgress(instance, content) {
    const waitRoot = instance.querySelector('[data-consultation-wait]');
    if (!waitRoot || instance.currentStep !== 'assessment-loading') {
      stopAssessmentWaitProgress(instance);
      return;
    }

    stopAssessmentWaitProgress(instance);

    const elements = getProgressElements(instance);
    const t = instance.config && instance.config.i18n || {};
    const durationMs = getAssessmentWaitDuration(instance);
    const startedAt = Date.now();
    const phrases = buildWaitStreamPhrases(instance, content);
    const state = {
      intervalId: null,
      streamTimeoutId: null,
      startFrameId: null,
      displayedPercent: 0,
      phraseIndex: 0,
      wordIndex: 0
    };

    instance._assessmentWaitProgressState = state;
    updateProgress(instance, 0);
    if (elements.label) elements.label.textContent = t.generatingAssessment || t.phaseGenerating || '';

    function scheduleStream(delay) {
      state.streamTimeoutId = setTimeout(streamNextWord, delay);
    }

    function streamNextPhrase() {
      if (!phrases.length || !elements.stream) return;
      state.wordIndex = 0;
      const phrase = phrases[state.phraseIndex % phrases.length];
      state.currentWords = phrase.split(/\s+/).filter(Boolean);
      state.phraseIndex += 1;
      streamNextWord();
    }

    function streamNextWord() {
      if (!elements.stream || !state.currentWords || !state.currentWords.length) return;
      state.wordIndex += 1;
      elements.stream.textContent = state.currentWords.slice(0, state.wordIndex).join(' ');

      if (state.wordIndex < state.currentWords.length) {
        scheduleStream(70 + Math.floor(Math.random() * 80));
        return;
      }

      state.currentWords = null;
      if (phrases.length) {
        state.streamTimeoutId = setTimeout(streamNextPhrase, 650 + Math.floor(Math.random() * 700));
      }
    }

    if (elements.stream) {
      elements.stream.textContent = '';
      if (phrases.length) streamNextPhrase();
    }

    state.intervalId = setInterval(function tickProgress() {
      if (instance.currentStep !== 'assessment-loading') {
        stopAssessmentWaitProgress(instance);
        return;
      }

      const elapsedRatio = Math.min(1, (Date.now() - startedAt) / durationMs);
      const curve = 1 - Math.pow(1 - elapsedRatio, 2.15);
      const jitter = Math.sin((Date.now() - startedAt) / 390) * 1.8 + Math.random() * 1.6;
      const cap = elapsedRatio >= 1 ? 97 : 96;
      const target = Math.min(cap, Math.floor(curve * 96 + jitter));
      state.displayedPercent = Math.max(state.displayedPercent, target);
      updateProgress(instance, state.displayedPercent);
    }, 240);
  }

  function scheduleAssessmentWaitProgress(instance, content) {
    stopAssessmentWaitProgress(instance);
    updateProgress(instance, 0);

    const state = { startFrameId: null };
    instance._assessmentWaitProgressState = state;
    state.startFrameId = requestAnimationFrame(function onFrame() {
      startAssessmentWaitProgress(instance, content);
    });
  }

  async function completeAssessmentWaitProgress(instance) {
    stopAssessmentWaitProgress(instance, { complete: true });
    await new Promise(function settle(resolve) {
      setTimeout(resolve, 220);
    });
  }

  function renderAssessmentWait(instance, waitExperience) {
    const t = instance.config && instance.config.i18n || {};
    const content = normalizeWaitExperience(instance, waitExperience);
    const waitRoot = instance.querySelector('[data-consultation-wait]');
    const contactRoot = instance.querySelector('[data-consultation-contact-surface]');

    instance._waitExperience = content;

    if (waitRoot) waitRoot.dataset.shownAt = String(Date.now());
    renderCompletionSurface(
      instance,
      contactRoot,
      CONTACT_STAGE_SELECTORS,
      content,
      t.analysisReady || 'Your personalized consultation is ready'
    );
    renderCompletionSurface(
      instance,
      waitRoot,
      WAIT_STAGE_SELECTORS,
      content,
      t.generatingAssessment || t.phaseGenerating || 'Preparing your personalized consultation'
    );
    renderDeliveryPanel(instance);
    scheduleAssessmentWaitProgress(instance, content);
  }

  function continueWithoutContact(instance) {
    instance._contact = null;
    instance._email = null;
    instance._contactOffer = null;

    const contactCapturePromise = startAnonymousContactCapture(instance);
    enterAssessmentWait(instance, instance._waitExperience, 'forward', contactCapturePromise);
  }

  function startAnonymousContactCapture(instance) {
    const completion = window.TaraConsultationCompletion;
    if (!completion || typeof completion.sendContactCapture !== 'function') return null;
    if (typeof completion.getOfferSettingsPayload === 'function') {
      const offerSettings = completion.getOfferSettingsPayload(instance);
      if (!offerSettings || !offerSettings.offer_discount_for_full_set) return null;
    }

    const fbPayload = typeof instance._getFacebookTrackingPayload === 'function'
      ? instance._getFacebookTrackingPayload()
      : {};

    return completion.sendContactCapture(instance, null, false, fbPayload);
  }

  function enterContactCapture(instance, waitExperience, direction) {
    if (!instance._emailForm) {
      enterAssessmentWait(instance, waitExperience, direction || 'forward');
      return;
    }

    if (!instance._assessmentPromise && !instance._assessmentResult) {
      instance._fireAssessmentEarly();
    }

    instance._configureContactStep();
    if (instance._shouldSkipContactCapture()) {
      continueWithoutContact(instance);
      return;
    }

    renderAssessmentWait(instance, waitExperience);
    instance._showStep('email', direction || 'forward');

    const contactInput = instance._getContactInput();
    if (contactInput) {
      contactInput.disabled = false;
      instance._syncContactInput();
      requestAnimationFrame(function focusContact() {
        contactInput.focus();
      });
    }
  }

  function enterAssessmentWait(instance, waitExperience, direction, contactCapturePromise) {
    if (!instance._assessmentPromise && !instance._assessmentResult) {
      instance._fireAssessmentEarly();
    }

    renderAssessmentWait(instance, waitExperience);
    instance._showStep('assessment-loading', direction || 'forward');
    waitForAssessmentAndRedirect(instance, contactCapturePromise);
  }

  function hasAssessmentPayload(value) {
    return value && typeof value === 'object' && Object.keys(value).length > 0;
  }

  async function waitForAssessmentAndRedirect(instance, contactCapturePromise) {
    const runId = ++instance._assessmentWaitRunId;

    try {
      const assessmentPromise = instance._assessmentPromise
        ? instance._assessmentPromise.then(function resolveAssessment() { return instance._assessmentResult; })
        : instance._fetchAssessmentAsync();

      const pending = contactCapturePromise
        ? [assessmentPromise, contactCapturePromise]
        : [assessmentPromise];
      const settled = await Promise.allSettled(pending);
      const assessmentState = settled[0];
      const contactCaptureState = settled[1] || null;

      if (assessmentState.status === 'fulfilled') {
        instance._assessmentResult = assessmentState.value;
      } else {
        throw assessmentState.reason;
      }

      let finalResult = hasAssessmentPayload(instance._assessmentResult)
        ? instance._assessmentResult
        : null;

      if (contactCaptureState && contactCaptureState.status === 'fulfilled' && contactCaptureState.value) {
        instance._contactOffer = contactCaptureState.value.offer || null;
        const contactAssessment = contactCaptureState.value.assessment || contactCaptureState.value.assessmentResult || null;
        if (hasAssessmentPayload(contactAssessment)) {
          finalResult = contactAssessment;
        }
      } else if (contactCaptureState && contactCaptureState.status === 'rejected') {
        const code = String(contactCaptureState.reason?.errorCode || contactCaptureState.reason?.code || '').trim();
        if (contactCaptureState.reason?.status === 429 || code.indexOf('rate_limited_') === 0) {
          throw contactCaptureState.reason;
        }
      }

      await instance._ensureAssessmentWaitMinimum();
      await completeAssessmentWaitProgress(instance);

      if (runId !== instance._assessmentWaitRunId || instance.currentStep !== 'assessment-loading') return;

      if (finalResult) {
        instance._assessmentResult = finalResult;
        instance._storeAndRedirect(finalResult, instance._contact);
      } else if (instance._assessmentError) {
        throw instance._assessmentError;
      } else {
        throw new Error('Assessment payload missing');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (runId !== instance._assessmentWaitRunId) return;
      console.error('Consultation assessment failed:', err);
      instance._captureMonitoringException(err, {
        route: '/api/assessment',
        error_code: err && err.errorCode || 'assessment_request_failed',
        status_code: err && err.status || null,
        worker_error: err && (err.workerError || err.message) || '',
        tags: { consultation_action: 'assessment_wait' },
        fingerprint: ['tara-consultation', 'assessment-wait', err && (err.errorCode || err.name) || 'error']
      });
      instance._assessmentPromise = null;
      instance._assessmentResult = null;
      instance._assessmentError = null;
      stopAssessmentWaitProgress(instance);
      instance._renderAssessmentFailure(() => waitForAssessmentAndRedirect(instance), err);
    }
  }

  async function ensureAssessmentWaitMinimum(instance) {
    const waitRoot = instance.querySelector('[data-consultation-wait]');
    if (!waitRoot || instance.currentStep !== 'assessment-loading') return;

    const shownAt = Number(waitRoot.dataset.shownAt || 0);
    if (!shownAt) return;

    const remaining = getAssessmentWaitDuration(instance) - (Date.now() - shownAt);
    if (remaining > 0) {
      await new Promise(function delay(resolve) {
        setTimeout(resolve, remaining);
      });
    }
  }

  window.TaraConsultationWait = Object.freeze({
    normalizeWaitExperience,
    renderAssessmentWait,
    enterContactCapture,
    continueWithoutContact,
    enterAssessmentWait,
    waitForAssessmentAndRedirect,
    ensureAssessmentWaitMinimum,
    completeAssessmentWaitProgress,
    stopAssessmentWaitProgress,
  });
})();
